// helpers/generateYupValidationSchema.ts
import * as yup from 'yup';
import { isValidGlyphColor } from '../glyph/sphereGlyph';
import { SchemaProperty } from 'types/schema';

/**
 * Applies JSON Schema validation rules to a Yup schema
 * @param fieldSchema - The base Yup schema
 * @param jsonSchema - The JSON Schema definition
 * @param propertyName - The property name for error messages
 * @returns Enhanced Yup schema with JSON Schema validation
 */
function applyJsonSchemaValidation(fieldSchema: yup.Schema<any>, jsonSchema: any, propertyName: string): yup.Schema<any> {
  if (!jsonSchema || typeof jsonSchema !== 'object') {
    return fieldSchema;
  }

  // Handle object type with properties validation
  if (jsonSchema.type === 'object' && jsonSchema.properties) {
    const shape: Record<string, yup.Schema<any>> = {};
    
    // Process each property in the JSON Schema
    for (const [propName, propDef] of Object.entries(jsonSchema.properties as Record<string, any>)) {
      let propSchema: yup.Schema<any>;
      
      // Map JSON Schema types to Yup schemas
      switch (propDef.type) {
        case 'string': {
          let stringSchema = yup.string();
          
          // Apply string pattern validation
          if (propDef.pattern) {
            const regex = new RegExp(propDef.pattern);
            stringSchema = stringSchema.matches(regex, `${propertyName}.${propName} must match pattern: ${propDef.pattern}`);
          }
          
          // Apply string length constraints
          if (typeof propDef.minLength === 'number') {
            stringSchema = stringSchema.min(propDef.minLength, `${propertyName}.${propName} must be at least ${propDef.minLength} characters`);
          }
          if (typeof propDef.maxLength === 'number') {
            stringSchema = stringSchema.max(propDef.maxLength, `${propertyName}.${propName} must be at most ${propDef.maxLength} characters`);
          }
          
          propSchema = stringSchema;
          break;
        }
          
        case 'number':
        case 'integer': {
          let numberSchema = yup.number();
          
          // Apply numeric constraints
          if (typeof propDef.minimum === 'number') {
            numberSchema = numberSchema.min(propDef.minimum, `${propertyName}.${propName} must be at least ${propDef.minimum}`);
          }
          if (typeof propDef.maximum === 'number') {
            numberSchema = numberSchema.max(propDef.maximum, `${propertyName}.${propName} must be at most ${propDef.maximum}`);
          }
          
          propSchema = numberSchema;
          break;
        }
          
        case 'boolean':
          propSchema = yup.boolean();
          break;
          
        case 'array': {
          let arraySchema = yup.array();
          
          // Apply array length constraints
          if (typeof propDef.minItems === 'number') {
            arraySchema = arraySchema.min(propDef.minItems, `${propertyName}.${propName} must have at least ${propDef.minItems} items`);
          }
          if (typeof propDef.maxItems === 'number') {
            arraySchema = arraySchema.max(propDef.maxItems, `${propertyName}.${propName} must have at most ${propDef.maxItems} items`);
          }
          
          propSchema = arraySchema;
          break;
        }
          
        case 'object':
          propSchema = yup.object();
          // Could recursively apply nested object validation here if needed
          break;
          
        default:
          propSchema = yup.mixed();
      }
      
      shape[propName] = propSchema;
    }
    
    // Apply required fields validation
    if (Array.isArray(jsonSchema.required)) {
      for (const requiredField of jsonSchema.required) {
        if (shape[requiredField]) {
          shape[requiredField] = shape[requiredField].required(`${propertyName}.${requiredField} is required`);
        }
      }
    }
    
    // Return object schema with the defined shape
    return yup.object().shape(shape);
  }
  
  return fieldSchema;
}

/**
 * Generates a Yup validation schema shape from the merged schema properties.
 * @param properties - The merged properties of the schema.
 * @returns A Yup ObjectShape.
 */
export function generateYupValidationSchema(
  properties: SchemaProperty[]
): Record<string, yup.Schema<any>> {
  const yupSchemaFields: Record<string, yup.Schema<any>> = {};

  for (const property of properties) {
    let fieldSchema: yup.Schema<any>;

    try {
      // Map the property type to the corresponding Yup schema type
      switch (property.type.toLowerCase()) {
        case 'string':
          fieldSchema = yup.string();
          break;
        case 'number':
          fieldSchema = yup.number().typeError(`${property.name} must be a number`);
          break;
        case 'boolean':
          fieldSchema = yup.boolean();
          break;
        case 'date':
          fieldSchema = yup.date().typeError(`${property.name} must be a valid date`);
          break;
        case 'datetime':
          // Validate ISO 8601 datetime strings
          fieldSchema = yup
            .string()
            .matches(
              /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/,
              `${property.name} must be a valid ISO 8601 datetime string (e.g., 2025-10-02T14:30:00.000Z)`
            )
            .test(
              'is-valid-date',
              `${property.name} must be a valid datetime`,
              (value) => {
                if (!value) return true; // Let required validation handle empty values
                const date = new Date(value);
                return !Number.isNaN(date.getTime());
              }
            )
            .typeError(`${property.name} must be a valid date/time`);
          break;
        case 'array':
          fieldSchema = yup.array();
          break;
        case 'object':
        case 'json':
          fieldSchema = yup.object();
          break;
        case 'reference':
          // Reference value is a sphere id (string). Semantic validation (type / targetFilter / view)
          // runs server-side in updateSphereProperties (Phase 3). Here we only register the yup type
          // so saves don't hit the throwing `default` case.
          fieldSchema = yup.string();
          break;
        case 'unit':
          // Unit type accepts either a plain number or a UnitValue object
          fieldSchema = yup.mixed().test(
            'is-unit-value',
            `${property.name} must be a number or a valid unit value object`,
            (value) => {
              if (value === null || value === undefined) return true;
              // Accept plain numbers
              if (typeof value === 'number') return !Number.isNaN(value);
              // Accept UnitValue objects
              if (typeof value === 'object' && value !== null) {
                const obj = value as Record<string, unknown>;
                const objValue = obj['value'];
                const objUnit = obj['unit'];
                return (
                  typeof objValue === 'number' &&
                  !Number.isNaN(objValue) &&
                  (objUnit === undefined || typeof objUnit === 'string')
                );
              }
              return false;
            }
          );

          break;
        default:
          throw new Error(`Unsupported property type: ${property.type}`);
      }

      // Apply jsonSchema validation if present
      if (property.type === 'json' && property.jsonSchema) {
        fieldSchema = applyJsonSchemaValidation(fieldSchema, property.jsonSchema, property.name);
      }

      // Apply default value if provided
      if (property.default_value !== undefined) {
        if (property.default_value === null) {
          fieldSchema = fieldSchema.nullable().default(null);
        } else {
          fieldSchema = fieldSchema.default(property.default_value);
        }
      }

      // Apply required validation
      if (property.required) {
        fieldSchema = fieldSchema.required('This field is required');
      }

      // Apply nullable validation - must be checked AFTER required
      // A field can be required but nullable (meaning it must be present but can be null)
      if ((property as any).nullable === true) {
        fieldSchema = fieldSchema.nullable();
      }

      // Apply additional rules
      if (Array.isArray(property.rules)) {
        for (const rule of property.rules) {
          if (typeof rule.method !== 'string') continue;

          // Handle custom 'precision' rule (not a native Yup method)
          if (rule.method === 'precision') {
            const maxDecimals = rule.args[0] as number;
            fieldSchema = fieldSchema.test(
              'precision',
              `${property.name} must have at most ${maxDecimals} decimal places`,
              (value) => {
                if (value === null || value === undefined) return true;
                // Extract numeric value from plain number or UnitValue object
                let numValue: unknown = null;
                if (typeof value === 'number') {
                  numValue = value;
                } else if (typeof value === 'object' && value !== null) {
                  numValue = (value as Record<string, unknown>)['value'];
                }
                if (numValue === null || numValue === undefined) return true;
                if (typeof numValue !== 'number') return true;
                // Check decimal places
                const numStr = numValue.toString();
                const decimalIndex = numStr.indexOf('.');
                if (decimalIndex === -1) return true; // No decimal places
                const decimalPlaces = numStr.length - decimalIndex - 1;
                return decimalPlaces <= maxDecimals;
              }
            );
          } else if (rule.method === 'glyphColor') {
            // Custom rule: the colour of a sphere's own glyph (center_glyph.glyph_color).
            // JSON-schema enum/additionalProperties are not applied above, so this is the real check;
            // isValidGlyphColor rejects any key other than kind and value.
            fieldSchema = fieldSchema.test(
              'glyphColor',
              `${property.name} must be a valid glyph colour`,
              (value) => value === null || value === undefined || isValidGlyphColor(value)
            );
          } else if (rule.method === 'min' && property.type.toLowerCase() === 'unit') {
            // Handle 'min' rule for unit type (which uses yup.mixed())
            const minValue = rule.args[0] as number;
            fieldSchema = fieldSchema.test(
              'min',
              `${property.name} must be at least ${minValue}`,
              (value) => {
                if (value === null || value === undefined) return true;
                let numValue: number | null = null;
                if (typeof value === 'number') {
                  numValue = value;
                } else if (typeof value === 'object' && value !== null) {
                  const objValue = (value as Record<string, unknown>)['value'];
                  if (typeof objValue === 'number') numValue = objValue;
                }
                if (numValue === null) return true;
                return numValue >= minValue;
              }
            );
          } else if (rule.method === 'max' && property.type.toLowerCase() === 'unit') {
            // Handle 'max' rule for unit type (which uses yup.mixed())
            const maxValue = rule.args[0] as number;
            fieldSchema = fieldSchema.test(
              'max',
              `${property.name} must be at most ${maxValue}`,
              (value) => {
                if (value === null || value === undefined) return true;
                let numValue: number | null = null;
                if (typeof value === 'number') {
                  numValue = value;
                } else if (typeof value === 'object' && value !== null) {
                  const objValue = (value as Record<string, unknown>)['value'];
                  if (typeof objValue === 'number') numValue = objValue;
                }
                if (numValue === null) return true;
                return numValue <= maxValue;
              }
            );
          } else if (rule.method === 'maxLength' || rule.method === 'minLength') {
            // JSON-schema-style length methods map to Yup's contextual string max/min
            // (which constrain string LENGTH). Guard args so a malformed rule is a no-op.
            const yupMethod = rule.method === 'maxLength' ? 'max' : 'min';
            const lengthArgs = Array.isArray(rule.args) ? rule.args : [];
            if (lengthArgs.length > 0 && typeof (fieldSchema as any)[yupMethod] === 'function') {
              fieldSchema = (fieldSchema as any)[yupMethod](...lengthArgs);
            }
          } else if (rule.method === 'pattern') {
            // JSON-schema-style regex maps to Yup's `matches`. This mapping is
            // required, not cosmetic: `matches` takes a RegExp OBJECT, which JSON
            // cannot express — so a schema author can only ever write `pattern`
            // with a string arg, and without this it silently no-ops and warns.
            const patternArgs = Array.isArray(rule.args) ? rule.args : [];
            const source = patternArgs[0];
            if (typeof source === 'string' && typeof (fieldSchema as any).matches === 'function') {
              try {
                // excludeEmptyString is required, not optional polish: Yup's
                // `matches` REJECTS '' by default, which would make every
                // optional pattern-validated property mandatory at create time.
                // A property that is set later (e.g. by the saga's
                // apply_properties step) is empty at sphere creation, and
                // without this the create fails validation. Requiredness is
                // `required`'s job — a pattern only constrains a value that
                // is actually present.
                fieldSchema = (fieldSchema as any).matches(new RegExp(source), {
                  excludeEmptyString: true,
                });
              } catch {
                // An invalid regex is an authoring error; skip the rule rather
                // than blow up every create/update against this schema.
                if (process.env['NODE_ENV'] !== 'test') {
                  console.warn(
                    `Invalid 'pattern' regex '${source}' for property '${property.name}' — rule skipped`
                  );
                }
              }
            }
          } else if (typeof (fieldSchema as any)[rule.method] === 'function') {
            // Apply native Yup method (min, max, positive, integer, etc.). Some take no
            // args (positive, integer), so default to [] — otherwise a rule that omitted
            // `args` makes `...rule.args` throw "rule.args is not iterable".
            fieldSchema = (fieldSchema as any)[rule.method](...(Array.isArray(rule.args) ? rule.args : []));
          } else {
            // Only warn in development, not during tests
            if (process.env['NODE_ENV'] !== 'test') {
              console.warn(
                `Unsupported rule method '${rule.method}' for property '${property.name}'`
              );
            }
          }
        }
      }

      // Assign the field schema to the Yup schema fields
      yupSchemaFields[property.name] = fieldSchema;
    } catch (error) {
      console.error(
        `Failed to process property "${property.name}": ${(error as Error).message}`
      );
      // Assign a fallback schema in case of errors
      yupSchemaFields[property.name] = yup.mixed().default(property.default_value);
    }
  }

  // Return the schema shape without wrapping it in yup.object().shape
  return yupSchemaFields;
}
